//external
import { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Help from '@material-ui/icons/Help';
import { useSelector, useDispatch } from 'react-redux';
import ExitToAppRoundedIcon from '@material-ui/icons/ExitToAppRounded';
import { useNavigate, Link, useSubmit } from 'react-router-dom';
import { MdSettings } from 'react-icons/md';
import {
    Avatar,
    AvatarBadge,
    FormControl,
    Tooltip as CTooltip,
    IconButton as CIconButton,
    Menu,
    MenuButton,
    MenuDivider,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    Badge,
    Flex,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon } from '@chakra-ui/icons';
import { Select } from 'chakra-react-select';

//internal
import { CompanyStatus, Searchbar } from './';
import { cannotNavigate } from '../../store/middlewares/modalChangesMiddleware';
import config from '../../config.json';
import { axios, checkToken } from '../../services/axios';
import useURLRewrite from 'hooks/useURLRewrite';
import {
    Button,
    Icon,
    MenuItem,
    MenuList,
    MenuText,
    Modal,
    ModalOverlay,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalContent,
    ThemeGroup,
    FormLabel,
} from '@/components/v4';
import walkthroughIds from './walkthroughIds';
import { navbarSelectAccents, supportTooltipLabel } from './constant';
import { classNames } from 'utils/classNames';
import { useLayoutContext } from 'context/LayoutContext';
import './styles.css';

import {
    chakraSelectDarkMode,
    defaultChakraSelectStyle,
    disabledBgStyle,
} from '@/constants';
import { floatingSelectStyles as _floatingSelectStyles } from '@/chakratheme';
import { changeToDarkTheme, changeToLightTheme } from '@/store/reducers/theme';
import ScopeType from 'utils/enums/ScopeType';

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            display: 'flex',
        },

        sectionDesktop: {
            [theme.breakpoints.up('xs')]: {
                display: 'flex',
            },
        },

        supportLink: {
            '& svg': {
                color: (props) => props.color,
            },
        },
    }),
);

const PAGES_NOT_USING_REDUX = [
    'services',
    'service-profiles',
    'call-stats',
    'jobs',
    'visibility-groups',
    'invoices',
];

const Navbar = () => {
    const [tokenState, setTokenState] = useState({
        isTokenExpired: false,
        message: '',
    });
    const navigate = useNavigate();
    const btnRef = useRef();
    const { color, handleColorChange } = useLayoutContext();

    const handleProfileMenuOpen = () => {};

    const handleMenuClose = () => {};

    const dispatch = useDispatch();
    const {
        mixedMode: lightMode,
        darkMode,
        colorScheme,
        ...rest
    } = useSelector((state) => {
        return { ...state.settings };
    });
    const { show, state, hash } = useSelector((state) => {
        return { ...state.modal };
    });

    const { invoiceType, loading: tableLoading } = useSelector((state) => {
        return { ...state.table };
    });
    const {
        minimumScope,
        currentScope,
        requiredScope,
        email,
        extLinkSupport,
        expiration,
        refreshExpiration,
    } = useSelector((state) => {
        return { ...state.login };
    });

    const chakraSelectDark = chakraSelectDarkMode(true);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...navbarSelectAccents(colorScheme, chakraSelectDark),
    };
    const floatingSelectStyles = _floatingSelectStyles(true);

    const INTERVAL_IN_MS = 1000;
    const initialCountDown = 5 * INTERVAL_IN_MS;
    const [countDown, setCountDown] = useState(initialCountDown);
    const { onClose } = useDisclosure();
    const {
        isOpen: isDrawerOpen,
        // onOpen: onDrawerOpen,
        onClose: onDrawerClose,
    } = useDisclosure();
    const [oldColor] = useState(colorScheme);
    const submit = useSubmit();

    const { rewrittenURL, trialStatus } = useURLRewrite({
        url: extLinkSupport,
    });

    const classes = useStyles({ color: trialStatus.colorScheme });

    const scopeList = () => {
        var scopeEnum = [];
        for (var value = minimumScope; value <= requiredScope; value += 20) {
            scopeEnum.push({ value, name: config.scope.json[value] });
        }
        // Numbers page (general list and platform view) only supports
        // Company/Partner scope, same restriction as platform numbers.
        if (window.location.pathname.includes('/numbers')) {
            scopeEnum = scopeEnum.filter(
                (item) => item.value <= ScopeType.PARTNER,
            );
        }
        return scopeEnum;
    };
    const pathnameWhitelisted =
        (window.location.pathname.split('/')?.length >= 3 &&
            (PAGES_NOT_USING_REDUX.includes(
                window.location.pathname.split('/')[2],
            ) ||
                window.location.pathname.split('/')[2] ===
                    'international-rates')) ||
        ['/users'].some((path) =>
            path.includes(location?.pathname?.split('/')[2]),
        );
    // Custom logic for International Rates List: disable dropdown in tab view or edit modal
    let scopeDropdownDisabled = pathnameWhitelisted ? false : tableLoading;
    if (window.location.pathname.includes('/international-rates')) {
        // Try to detect tab view or modal open via global state (window or Redux)
        // For simplicity, use a global flag if set by InternationalRatesList
        if (window.__intlRatesTabViewActive || window.__intlRatesModalOpen) {
            scopeDropdownDisabled = true;
        } else {
            scopeDropdownDisabled = false;
        }
    }
    // Lock scope dropdown for partner-only pages
    if (window.location.pathname.includes('/approval-codes')) {
        scopeDropdownDisabled = true;
        // Auto-switch to Partner scope if not already
        if (currentScope !== 40) {
            dispatch({
                type: 'CHANGE_CURRENT_SCOPE',
                payload: 40,
            });
        }
    }
    // Lock scope dropdown for company-only pages
    if (
        window.location.pathname.includes('/call-stats') ||
        window.location.pathname.includes('/calls') ||
        window.location.pathname.includes('/users') ||
        window.location.pathname.includes('/trunks') ||
        window.location.pathname.includes('/pbx') ||
        window.location.pathname.includes('/aors') ||
        window.location.pathname.includes('/security') ||
        window.location.pathname.includes('/dashboard-services')
    ) {
        scopeDropdownDisabled = true;
        // Auto-switch to Company scope if not already
        if (currentScope !== 20) {
            dispatch({
                type: 'CHANGE_CURRENT_SCOPE',

                payload: 20,
            });
        }
    }
    // Numbers page: scope dropdown is never force-locked, but scopeList()
    // above narrows the OPTIONS to Company/Partner for the whole page
    // (general list and platform view alike) — the Numbers table also
    // auto-defaults scope to Partner if the user is at Wholesaler/Global
    // when on this page.
    if (window.location.pathname.includes('/numbers')) {
        scopeDropdownDisabled = false;
    }
    // Lock admins page to company scope, except for Permissions tab or permissions edit page
    if (window.location.pathname.includes('/admins')) {
        // Unlock if on Permissions tab (flag set by Admins page) or on permissions edit page
        if (
            window.__adminsPermissionsTabActive ||
            (window.location.pathname.includes('/permissions/') &&
                window.location.pathname.includes('/edit'))
        ) {
            scopeDropdownDisabled = false;
        } else {
            scopeDropdownDisabled = true;
            // Auto-switch to Company scope if not already
            if (currentScope !== 20) {
                dispatch({
                    type: 'CHANGE_CURRENT_SCOPE',
                    payload: 20,
                });
            }
        }
    }
    // Always enable scope dropdown for jobs page
    if (
        window.location.pathname.includes('/jobs') ||
        window.location.pathname.includes('/number-blocks')
    ) {
        scopeDropdownDisabled = false;
    }

    const updateThemeSettings = (newModes) => {
        // Apply the mode change immediately; the Theme component swaps the
        // CSS and syncs Chakra's color mode reactively, so no reload needed.
        dispatch({
            type: 'CHANGE_MODE',
            payload: newModes,
        });

        dispatch(
            newModes.darkMode ? changeToDarkTheme() : changeToLightTheme(),
        );
        const lastUpdate = Date.now();
        const data = {
            settings: { ...rest, ...newModes, lastUpdate },
        };

        const jsonString = `"${JSON.stringify(data).replaceAll('"', '\\"')}"`;
        axios
            .post('/adminuser/uisettings', jsonString, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .catch((error) => {
                console.error('Failed to save theme settings:', error);
            });
    };

    // TODO: If the user has old light mode, defaults them into new light mode. To be deleted eventually.
    useEffect(() => {
        if (darkMode === false && lightMode === false) {
            const newModes = {
                darkMode: false,
                mixedMode: true,
            };

            updateThemeSettings(newModes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const menuOnClick = () => {
        if (
            cannotNavigate({
                show,
                state,
                hash,
                message:
                    'Are you sure you would like to change appearance? All unsaved changes will be lost.',
            })
        ) {
            return;
        }

        const newModes = lightMode
            ? {
                  darkMode: true,
                  mixedMode: false,
              }
            : {
                  darkMode: false,
                  mixedMode: true,
              };

        updateThemeSettings(newModes);
    };
    const themeOnClick = (e) => {
        e.preventDefault();
        onDrawerClose();
        const lastUpdate = Date.now();
        const data = {
            settings: { ...rest, colorScheme: color, lastUpdate },
        };
        const jsonString = `"${JSON.stringify(data).replaceAll('"', '\\"')}"`;
        axios
            .post('/adminuser/uisettings', jsonString, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .then(() => {
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            });
    };
    const handleDrawerClose = () => {
        handleColorChange(oldColor);
        onDrawerClose();
    };

    const toolbarOnChange = ({ value: v }) => {
        dispatch({
            type: 'CHANGE_CURRENT_SCOPE',
            payload: v,
        });
        ((v === 40 && invoiceType === 2) || (v === 20 && invoiceType > 0)) &&
            dispatch({
                type: 'CHANGE_TABLE_INVOICE_TYPE',
                payload: 0,
            });
    };

    const settingsOnClick = () => {
        navigate('/settings');
        handleMenuClose();
    };

    const activeLogout = () => {
        console.log('201: logout success!');
        if (
            cannotNavigate({
                show: show,
                state: state,
                hash: hash,
                message:
                    'Are you sure you would like to logout? All unsaved changes will be lost.',
            })
        )
            return;
        sessionStorage.clear();
        localStorage.clear();
        submit(null, { method: 'get', action: '/logout' });
        dispatch({ type: 'CLEAR_TOKEN' });
        dispatch({ type: 'LOGOUT' });
    };

    const logoutOnClick = useCallback(() => {
        console.log('201: logout success!');
        axios.get('/logout').finally(() => {
            sessionStorage.clear();
            localStorage.clear();
            dispatch({ type: 'CLEAR_TOKEN' });
            dispatch({ type: 'LOGOUT' });
            navigate('/');
        });
    }, [dispatch, navigate]);

    // NOTE: important check, app will break if this flag is not here, set state to VALID or UNKNOWN to remove the feature
    // const checkTokenCase = 'REFRESH';
    const checkTokenCase = checkToken();
    useEffect(() => {
        switch (checkTokenCase) {
            case 'RELOGIN': {
                // token and refresh token both expired
                return setTokenState((p) => ({
                    ...p,
                    isTokenExpired: true,
                    message:
                        "We couldn't authenticate your session. Please login again.",
                }));
            }

            case 'REFRESH': {
                // token expired but refresh token is valid
                return setTokenState((p) => ({
                    ...p,
                    isTokenExpired: true,
                    message:
                        'Your token has expired. Please interact with the app to refresh your session.',
                }));
            }

            default: {
                // VALID or UNKNOWN
                return setTokenState((p) => ({
                    ...p,
                    isTokenExpired: false,
                    message: 'Account',
                }));
            }
        }
    }, [checkTokenCase]);

    const getLongInactivity = () => {
        let now = new Date();
        const convertedHours = 8 * 60 * 60 * INTERVAL_IN_MS; // 8 hours
        // NOTE: isLongInactivity considered after 8 hours of inactivity
        return (
            now - Date.parse(expiration) > convertedHours ||
            now - Date.parse(refreshExpiration) > convertedHours
        );
    };
    const isLongInactivity = getLongInactivity();

    useEffect(() => {
        const countDownUntilZero = () => {
            setCountDown((prev) => {
                if (prev <= 0) return 0;

                return prev - INTERVAL_IN_MS;
            });
        };
        if (checkTokenCase === 'RELOGIN' && !isLongInactivity) {
            const timer = setTimeout(countDownUntilZero, INTERVAL_IN_MS);
            if (countDown <= 0) {
                onClose();
                logoutOnClick();
            }

            return () => clearTimeout(timer);
        }
    }, [countDown, checkTokenCase, isLongInactivity, logoutOnClick, onClose]);

    return (
        <>
            <Toolbar
                style={{
                    paddingLeft: 10,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                }}>
                <Searchbar />
                &nbsp; <CompanyStatus />
                <div className={classes.root} />
                <div className={classes.sectionDesktop}></div>
                <div className={classes.sectionDesktop}>
                    {requiredScope > ScopeType.COMPANY && (
                        <div
                            style={{
                                marginRight: 12,
                                top: 5,
                                width: 150,
                                height: 40,
                            }}>
                            <FormControl
                                id="scope-dropdown"
                                data-walkthroughid={
                                    walkthroughIds.ufScopeDropdown
                                }
                                variant="floatingSelect"
                                sx={floatingSelectStyles}>
                                <Select
                                    options={scopeList()}
                                    isDisabled={scopeDropdownDisabled}
                                    onChange={toolbarOnChange}
                                    name="Scope"
                                    value={scopeList().find(
                                        (item) => item.value === currentScope,
                                    )}
                                    getOptionLabel={(option) => option.name}
                                    chakraStyles={chakraStyles}
                                    classNamePrefix="chakra-react-select"
                                    tabSelectsValue
                                    openMenuOnFocus
                                    placeholder=" "
                                    colorScheme={colorScheme}
                                    selectedOptionColor={colorScheme}
                                />
                                <FormLabel>Scope</FormLabel>
                            </FormControl>
                        </div>
                    )}

                    <Flex aria-label="navbar-icon-wrapper" sx={disabledBgStyle}>
                        {rewrittenURL && (
                            <>
                                <CTooltip
                                    label={supportTooltipLabel(trialStatus)}
                                    hasArrow
                                    closeOnMouseDown
                                    closeDelay={100}>
                                    <Link
                                        to={rewrittenURL}
                                        target="_blank"
                                        className={classNames(
                                            'text-white',
                                            'flex hover:decoration-transparent',
                                        )}>
                                        <CIconButton
                                            edge="end"
                                            aria-label="Support URL"
                                            aria-haspopup="true"
                                            color="inherit"
                                            className={classes.supportLink}
                                            data-walkthroughid={
                                                walkthroughIds.ufSupportLink
                                            }
                                            borderRadius="full"
                                            bg="none"
                                            _hover={{
                                                bg: 'whiteAlpha.300',
                                            }}
                                            _active={{
                                                bg: 'whiteAlpha.300',
                                            }}
                                            icon={<Help />}
                                        />
                                    </Link>
                                </CTooltip>
                            </>
                        )}

                        {/* Accounts Menu */}
                        <Menu>
                            <CTooltip
                                label={tokenState.message}
                                hasArrow
                                closeOnMouseDown
                                closeDelay={100}>
                                <MenuButton
                                    as={CIconButton}
                                    edge="end"
                                    aria-label="account of current user"
                                    aria-haspopup="true"
                                    onClick={handleProfileMenuOpen}
                                    color="white"
                                    data-walkthroughid={
                                        walkthroughIds.ufAccountSettingsBtn
                                    }
                                    borderRadius="full"
                                    className={classNames(
                                        'dark',
                                        tokenState.isTokenExpired
                                            ? 'spinner'
                                            : null,
                                    )}
                                    bg="none"
                                    icon={
                                        tokenState.isTokenExpired ? (
                                            <AccountCircle
                                                className={
                                                    tokenState.isTokenExpired
                                                        ? 'account-warning'
                                                        : null
                                                }
                                            />
                                        ) : (
                                            <Avatar
                                                size="xs"
                                                bg="black"
                                                w={5}
                                                h={5}
                                                color="white"
                                                icon={<AccountCircle />}>
                                                <AvatarBadge
                                                    boxSize="1em"
                                                    bg="green.500"
                                                />
                                            </Avatar>
                                        )
                                    }
                                    _hover={{
                                        bg: 'whiteAlpha.300',
                                    }}
                                    _active={{
                                        bg: 'whiteAlpha.300',
                                    }}
                                    rounded="full"
                                />
                            </CTooltip>
                            <MenuList
                                bg="dark.tcap"
                                sx={{
                                    '& button[role="menuitem"]:hover': {
                                        bg: 'rgba(255,255,255,0.16)',
                                    },
                                }}>
                                <MenuItem
                                    isDisabled
                                    icon={
                                        <Avatar
                                            name={email}
                                            size="xs"
                                            w={5}
                                            h={5}
                                        />
                                    }>
                                    <MenuText>{email}</MenuText>
                                </MenuItem>
                                <MenuDivider />
                                <MenuItem
                                    icon={
                                        <Icon
                                            as={lightMode ? MoonIcon : SunIcon}
                                            color={
                                                darkMode
                                                    ? 'rgba(255,255,255,1)'
                                                    : 'rgba(117,117,117,1)'
                                            }
                                        />
                                    }
                                    onClick={menuOnClick}>
                                    <MenuText>
                                        {lightMode
                                            ? 'Switch to Dark Mode'
                                            : 'Switch to Light Mode'}
                                    </MenuText>
                                </MenuItem>
                                {/* <MenuItem
                                    icon={
                                        <Icon
                                            as={BrushIcon}
                                            color={`${colorScheme || 'primary'}.500`}
                                        />
                                    }
                                    onClick={onDrawerOpen}>
                                    <MenuText>Theme</MenuText>
                                </MenuItem> */}
                                <MenuItem
                                    icon={
                                        <Icon
                                            as={MdSettings}
                                            color={
                                                darkMode
                                                    ? 'rgba(255,255,255,1)'
                                                    : 'rgba(117,117,117,1)'
                                            }
                                        />
                                    }
                                    onClick={settingsOnClick}>
                                    <MenuText>Settings</MenuText>
                                </MenuItem>
                                <MenuItem
                                    icon={
                                        <Icon
                                            as={ExitToAppRoundedIcon}
                                            color={
                                                darkMode
                                                    ? 'rgba(255,255,255,1)'
                                                    : 'rgba(117,117,117,1)'
                                            }
                                        />
                                    }
                                    onClick={activeLogout}>
                                    <MenuText>Logout</MenuText>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Flex>
                </div>
            </Toolbar>

            <Drawer
                isOpen={isDrawerOpen}
                placement="right"
                onClose={handleDrawerClose}
                initialFocusRef={btnRef}>
                <DrawerOverlay />
                <form onSubmit={themeOnClick}>
                    <DrawerContent bg="var(--chakra-colors-dark-bgDark)">
                        <DrawerCloseButton
                            color={`${colorScheme}.500`}
                            _hover={{
                                color: `${colorScheme}.600`,
                            }}
                        />
                        <DrawerHeader color={`${colorScheme || 'primary'}.500`}>
                            Change your{' '}
                            <Badge colorScheme={colorScheme || 'primary'}>
                                theme
                            </Badge>
                        </DrawerHeader>

                        <DrawerBody>
                            <ThemeGroup />
                        </DrawerBody>
                        <FormControl isRequired>
                            <FormLabel
                                color="white"
                                mt={2}
                                mx={0}
                                px={6}
                                fontSize="sm">
                                Press F5 to reload and preview theme changes.
                                Remember to save your changes for next login.
                            </FormLabel>
                        </FormControl>

                        <DrawerFooter>
                            <Button
                                className="secondary hover:opacity-80"
                                variant="outline"
                                mr={3}
                                onClick={handleDrawerClose}
                                colorScheme={colorScheme}>
                                Cancel
                            </Button>
                            <Button
                                ref={btnRef}
                                colorScheme={colorScheme}
                                minW="80px"
                                _hover={{
                                    bg: `${colorScheme}.600`,
                                }}
                                type="submit">
                                Save
                            </Button>
                        </DrawerFooter>
                    </DrawerContent>
                </form>
            </Drawer>
            <Modal
                isOpen={checkTokenCase === 'RELOGIN'}
                isCentered
                onClose={onClose}>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader px={5}>Timeout session!</ModalHeader>
                    <ModalBody px={5}>
                        {isLongInactivity ? (
                            <p>
                                You have been logged out due to inactivity.
                                Please login again.
                            </p>
                        ) : (
                            <p>
                                Your session has timed out. You will be signed
                                out in {countDown / INTERVAL_IN_MS} seconds
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {isLongInactivity && (
                            <Button
                                border={1}
                                borderColor="brand"
                                onClick={logoutOnClick}>
                                Login
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default Navbar;
